import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export type ActionType =
  | 'product_add'
  | 'product_update'
  | 'product_delete'
  | 'product_price_change'
  | 'product_stock_change'
  | 'product_name_change'
  | 'product_category_change'
  | 'order_status_change'
  | 'order_cancel'
  | 'discount_add'
  | 'discount_delete'
  | 'deal_add'
  | 'deal_update'
  | 'deal_delete'
  | 'admin_add'
  | 'admin_update'
  | 'admin_delete'
  | 'review_approve'
  | 'review_delete'
  | 'settings_update'
  | 'city_add'
  | 'city_update'
  | 'city_delete'
  | 'badge_add'
  | 'badge_delete';

export type EntityType = 'product' | 'order' | 'discount' | 'deal' | 'admin' | 'review' | 'settings' | 'city' | 'badge';

export interface ActivityEntry {
  id: number;
  action_type: ActionType;
  entity_type: EntityType;
  entity_id: string | null;
  entity_name: string | null;
  admin_email: string;
  admin_name: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface LogActivityParams {
  actionType: ActionType;
  entityType: EntityType;
  entityId?: string | number;
  entityName?: string;
  adminEmail: string;
  adminName?: string;
  details?: Record<string, unknown>;
}

export function useActivityLog() {
  const logActivity = useCallback(async (params: LogActivityParams): Promise<void> => {
    try {
      await supabase.from('activity_log').insert({
        action_type: params.actionType,
        entity_type: params.entityType,
        entity_id: params.entityId != null ? String(params.entityId) : null,
        entity_name: params.entityName ?? null,
        admin_email: params.adminEmail,
        admin_name: params.adminName ?? null,
        details: params.details ?? {},
      });
    } catch {
      // Silent — logging should never break the main flow
    }
  }, []);

  return { logActivity };
}

// ── Action metadata for display ──
export const ACTION_META: Record<ActionType, {
  label: string;
  icon: string;
  color: string;
  bg: string;
  border: string;
}> = {
  product_add:            { label: 'إضافة منتج',          icon: 'ri-add-circle-line',        color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  product_update:         { label: 'تعديل منتج',           icon: 'ri-edit-line',              color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  product_delete:         { label: 'حذف منتج',             icon: 'ri-delete-bin-line',        color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  product_price_change:   { label: 'تغيير سعر منتج',       icon: 'ri-price-tag-3-line',       color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  product_stock_change:   { label: 'تحديث مخزون',          icon: 'ri-archive-2-line',         color: 'text-sky-700',     bg: 'bg-sky-50',      border: 'border-sky-200' },
  product_name_change:    { label: 'تغيير اسم منتج',       icon: 'ri-text',                   color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  product_category_change:{ label: 'تغيير فئة منتج',       icon: 'ri-layout-grid-line',       color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  order_status_change:    { label: 'تغيير حالة طلب',       icon: 'ri-file-list-3-line',       color: 'text-[#1a1a1a]',  bg: 'bg-gray-50',     border: 'border-gray-200' },
  order_cancel:           { label: 'إلغاء طلب',            icon: 'ri-close-circle-line',      color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  discount_add:           { label: 'إضافة كود خصم',        icon: 'ri-coupon-3-line',          color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  discount_delete:        { label: 'حذف كود خصم',          icon: 'ri-coupon-3-line',          color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  deal_add:               { label: 'إضافة عرض',            icon: 'ri-flashlight-line',        color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  deal_update:            { label: 'تعديل عرض',            icon: 'ri-flashlight-line',        color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  deal_delete:            { label: 'حذف عرض',              icon: 'ri-flashlight-line',        color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  admin_add:              { label: 'إضافة مشرف',           icon: 'ri-user-add-line',          color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  admin_update:           { label: 'تعديل مشرف',           icon: 'ri-user-settings-line',     color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  admin_delete:           { label: 'حذف مشرف',             icon: 'ri-user-unfollow-line',     color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  review_approve:         { label: 'قبول تقييم',           icon: 'ri-star-line',              color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  review_delete:          { label: 'حذف تقييم',            icon: 'ri-star-line',              color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  settings_update:        { label: 'تحديث الإعدادات',      icon: 'ri-settings-3-line',        color: 'text-[#1a1a1a]',  bg: 'bg-gray-50',     border: 'border-gray-200' },
  city_add:               { label: 'إضافة مدينة',          icon: 'ri-map-pin-line',           color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  city_update:            { label: 'تعديل مدينة',          icon: 'ri-map-pin-line',           color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  city_delete:            { label: 'حذف مدينة',            icon: 'ri-map-pin-line',           color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
  badge_add:              { label: 'إضافة شارة',           icon: 'ri-price-tag-3-line',       color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  badge_delete:           { label: 'حذف شارة',             icon: 'ri-price-tag-3-line',       color: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200' },
};

export const ENTITY_LABELS: Record<EntityType, string> = {
  product:  'منتج',
  order:    'طلب',
  discount: 'خصم',
  deal:     'عرض',
  admin:    'مشرف',
  review:   'تقييم',
  settings: 'إعدادات',
  city:     'مدينة',
  badge:    'شارة',
};
